import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { getDefaultPathForTarget, type AppTarget } from '@/routes/targetRouting';

type AuthContinuePageProps = {
  appTarget: Exclude<AppTarget, 'invalid'>;
};

const REQUEST_TIMEOUT_MS = 30_000;

const normalizeUrl = (value?: string): string => String(value || '').trim().replace(/\/+$/, '');

const getCurrentHost = (): string => {
  if (typeof window === 'undefined') return 'admin.bassitai.com';
  return String(window.location.host || '').split(':')[0].toLowerCase();
};

const isLocalHost = (host: string): boolean =>
  host === 'localhost' || host === '127.0.0.1' || host.endsWith('.localhost');

const getBaseDomain = (host: string): string => {
  if (!host) return 'bassitai.com';
  if (isLocalHost(host)) return 'localhost';
  const parts = host.split('.').filter(Boolean);
  if (parts.length < 2) return host;
  return parts.slice(-2).join('.');
};

const buildLoginUrl = (): string => {
  if (typeof window === 'undefined') return 'https://login.bassitai.com/login';
  const host = getCurrentHost();
  const base = getBaseDomain(host);
  const protocol = isLocalHost(host) ? 'http' : 'https';
  const loginHost = base === 'localhost' ? 'login.localhost' : `login.${base}`;
  const returnTo = String(window.location.host || host);
  return `${protocol}://${loginHost}/login?returnTo=${encodeURIComponent(returnTo)}`;
};

const getApiBaseUrl = (): string => {
  if (typeof window !== 'undefined') {
    const runtime = (window as any).__RUNTIME_CONFIG__ as Record<string, unknown> | undefined;
    const runtimeBase = normalizeUrl(String(runtime?.API_BASE_URL || runtime?.apiBaseUrl || ''));
    if (runtimeBase) {
      return runtimeBase.endsWith('/api') ? runtimeBase : `${runtimeBase}/api`;
    }

    const host = String(window.location.hostname || '').toLowerCase();
    const protocol = window.location.protocol || 'https:';
    const port = String(window.location.port || '').trim();
    const portSuffix = port ? `:${port}` : '';
    if (isLocalHost(host)) {
      return `${protocol}//api.localhost${portSuffix}/api`;
    }
    const parts = host.split('.').filter(Boolean);
    if (parts.length >= 2) {
      const base = parts.slice(-2).join('.');
      return `${protocol}//api.${base}${portSuffix}/api`;
    }
  }

  const envBase = normalizeUrl(process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_API_URL);
  if (envBase) return envBase.endsWith('/api') ? envBase : `${envBase}/api`;
  return '/api';
};

const decodeJwtPayload = (token: string): Record<string, unknown> | null => {
  try {
    const payload = String(token || '').split('.')[1] || '';
    if (!payload) return null;
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    return JSON.parse(atob(padded));
  } catch (_) {
    return null;
  }
};

const sanitizeReturnToPath = (rawValue: string | null, fallbackPath: string): string => {
  const value = String(rawValue || '').trim();
  if (!value) return fallbackPath;
  if (value.startsWith('/') && !value.startsWith('//')) return value;
  if (typeof window !== 'undefined' && /^https?:\/\//i.test(value)) {
    try {
      const target = new URL(value);
      if (target.origin === window.location.origin) {
        return `${target.pathname}${target.search}${target.hash}`;
      }
    } catch (_) {
      // no-op
    }
  }
  return fallbackPath;
};

const extractErrorMessage = (payload: any, fallback: string): string =>
  String(
    payload?.error?.message ||
      payload?.message ||
      payload?.data?.message ||
      payload?.data?.error ||
      payload?.error ||
      fallback
  );

const fetchWithTimeout = async (url: string, init: RequestInit, timeoutMs: number): Promise<Response> => {
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(url, {
      ...init,
      signal: controller.signal,
    });
  } finally {
    window.clearTimeout(timeoutId);
  }
};

const persistSession = (data: any) => {
  const accessToken = String(data?.accessToken || data?.token || '').trim();
  if (!accessToken) {
    throw new Error('No access token returned from sign-in continuation.');
  }

  const refreshToken = String(data?.refreshToken || '').trim();
  const payload = decodeJwtPayload(accessToken) || {};
  const userFromPayload = {
    id: String(payload.sub || data?.user?.id || ''),
    email: String(payload.email || data?.user?.email || ''),
    name: String(payload.name || data?.user?.name || data?.user?.email || ''),
    role: String(payload.role || data?.user?.role || ''),
    systemRole: String(payload.systemRole || data?.user?.systemRole || ''),
    tenantId: String(payload.tenantId || data?.tenantId || data?.user?.tenantId || ''),
    tenantSlug: String(payload.tenantSlug || data?.tenantSlug || data?.user?.tenantSlug || ''),
  };

  const exp = Number(payload.exp || 0);
  const expiresAt = exp > 0 ? exp * 1000 : Date.now() + 60 * 60 * 1000;

  localStorage.setItem('auth_token', accessToken);
  localStorage.setItem('auth_v2_accessToken', accessToken);
  localStorage.setItem('accessToken', accessToken);
  localStorage.setItem('token', accessToken);
  if (refreshToken) {
    localStorage.setItem('refresh_token', refreshToken);
    localStorage.setItem('auth_v2_refreshToken', refreshToken);
    localStorage.setItem('refreshToken', refreshToken);
  }
  localStorage.setItem('user', JSON.stringify(userFromPayload));
  localStorage.setItem(
    'authData',
    JSON.stringify({
      token: accessToken,
      user: userFromPayload,
      tenantId: userFromPayload.tenantId || null,
      expiresAt,
    })
  );
  if (userFromPayload.tenantId) localStorage.setItem('tenantId', userFromPayload.tenantId);
  if (userFromPayload.tenantSlug) localStorage.setItem('tenantSlug', userFromPayload.tenantSlug);
};

export default function AuthContinuePage({ appTarget }: AuthContinuePageProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [inFlight, setInFlight] = useState(true);

  useEffect(() => {
    let active = true;

    const run = async () => {
      const params = new URLSearchParams(location.search);
      const token = String(params.get('token') || '').trim();
      const fallbackPath = getDefaultPathForTarget(appTarget);
      const returnTo = sanitizeReturnToPath(
        params.get('returnPath') || params.get('returnTo'),
        fallbackPath
      );

      if (!token) {
        if (active) {
          setError('Your sign-in link is missing a token. Please sign in again.');
          setInFlight(false);
        }
        return;
      }

      try {
        const response = await fetchWithTimeout(
          `${getApiBaseUrl()}/auth/continue`,
          {
            method: 'POST',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            body: JSON.stringify({ token }),
          },
          REQUEST_TIMEOUT_MS
        );

        const payload = await response.json().catch(() => ({}));
        const data = payload?.data ?? payload;
        if (!response.ok || payload?.success === false) {
          throw new Error(extractErrorMessage(payload, 'Unable to complete sign-in.'));
        }

        persistSession(data);
        navigate(returnTo, { replace: true });
      } catch (err: any) {
        if (!active) return;
        if (err?.name === 'AbortError') {
          setError('Sign-in timed out after 30 seconds. Please try again.');
        } else {
          setError(err?.message || 'Unable to complete sign-in. Please try again.');
        }
      } finally {
        if (active) {
          setInFlight(false);
        }
      }
    };

    void run();

    return () => {
      active = false;
    };
  }, [appTarget, location.search, navigate]);

  if (inFlight && !error) {
    return null;
  }

  if (!error) {
    return null;
  }

  return (
    <main
      style={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        background: '#0b1220',
        color: '#e5edf8',
        padding: 24,
        fontFamily: 'Inter, system-ui, sans-serif',
      }}
    >
      <section
        style={{
          width: '100%',
          maxWidth: 440,
          borderRadius: 14,
          border: '1px solid rgba(148, 163, 184, 0.25)',
          background: 'rgba(17, 24, 39, 0.92)',
          padding: 24,
        }}
      >
        <h1 style={{ margin: 0, fontSize: 24, color: '#fff' }}>Unable to finish sign-in</h1>
        <p style={{ marginTop: 12, marginBottom: 18, color: '#9fb0ca', lineHeight: 1.5 }}>{error}</p>
        <button
          type="button"
          onClick={() => window.location.replace(buildLoginUrl())}
          style={{
            border: 'none',
            borderRadius: 9,
            background: '#f97316',
            color: '#fff',
            fontWeight: 700,
            padding: '10px 14px',
            cursor: 'pointer',
          }}
        >
          Return to login
        </button>
      </section>
    </main>
  );
}
